import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users, UserCog, Search, Edit2, Trash2, X, Key, Ban, Mail, Phone, ShoppingBag, Gift, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UsersManagement = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '', loyalty_points: 0 });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data);
    } catch { toast.error('Üyeler yüklenemedi'); } finally { setLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/reset-password`, 
        { new_password: newPassword }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Şifre sıfırlandı');
      setShowResetPassword(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Şifre sıfırlanamadı'); }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-status`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(currentStatus ? 'Kullanıcı devre dışı bırakıldı' : 'Kullanıcı aktif edildi');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem başarısız'); }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}`, editForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kullanıcı güncellendi');
      setShowEditDialog(false);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Güncelleme başarısız'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kullanıcı silindi');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Silme başarısız'); }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({ name: user.name, phone: user.phone, loyalty_points: user.loyalty_points || 0 });
    setShowEditDialog(true);
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.phone?.includes(searchTerm)
  );

  const regularUsers = filteredUsers.filter(u => !u.is_admin);
  const totalOrders = users.reduce((sum, u) => sum + (u.order_count || 0), 0);
  const totalPoints = users.reduce((sum, u) => sum + (u.loyalty_points || 0), 0);

  if (loading) return <div className="text-center py-8">Yükleniyor...</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-basil/10 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-basil" />
            </div>
            <div>
              <p className="text-sm text-charcoal/60">Toplam Üye</p>
              <p className="font-playfair text-2xl font-bold text-charcoal">{regularUsers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-terracotta" />
            </div>
            <div>
              <p className="text-sm text-charcoal/60">Toplam Sipariş</p>
              <p className="font-playfair text-2xl font-bold text-terracotta">{totalOrders}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Gift className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-charcoal/60">Toplam Puan</p>
              <p className="font-playfair text-2xl font-bold text-amber-600">{totalPoints}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-charcoal/60">Aktif Üyeler</p>
              <p className="font-playfair text-2xl font-bold text-green-600">{regularUsers.filter(u => u.is_active !== false).length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-charcoal/40" />
          <Input
            placeholder="İsim, e-posta veya telefon ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-stone-100">
          <h2 className="font-playfair text-xl font-semibold">Üye Listesi</h2>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Üye</TableHead>
                <TableHead>E-posta</TableHead>
                <TableHead>Telefon</TableHead>
                <TableHead>Sipariş</TableHead>
                <TableHead>Puan</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id} className={user.is_admin ? 'bg-amber-50' : ''}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${user.is_admin ? 'bg-amber-500' : 'bg-basil'}`}>
                        {user.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        {user.is_admin && <span className="text-xs text-amber-600 font-medium">Admin</span>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-charcoal/70">{user.email}</TableCell>
                  <TableCell className="text-charcoal/70">{user.phone || '-'}</TableCell>
                  <TableCell><span className="font-semibold">{user.order_count || 0}</span></TableCell>
                  <TableCell><span className="font-semibold text-amber-600">{user.loyalty_points || 0}</span></TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {user.is_active !== false ? 'Aktif' : 'Pasif'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {!user.is_admin && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => openEditDialog(user)} title="Düzenle">
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setSelectedUser(user); setShowResetPassword(true); }} title="Şifre Sıfırla">
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleToggleStatus(user.id, user.is_active !== false)} title={user.is_active !== false ? 'Devre Dışı Bırak' : 'Aktif Et'}>
                            <Ban className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteUser(user.id)} title="Sil">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {filteredUsers.length === 0 && (
          <div className="p-8 text-center text-charcoal/60">
            {searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz üye yok'}
          </div>
        )}
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Şifre Sıfırla</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-charcoal/70">
              <strong>{selectedUser?.name}</strong> ({selectedUser?.email}) için yeni şifre belirleyin.
            </p>
            <div>
              <Label>Yeni Şifre</Label>
              <Input 
                type="password" 
                value={newPassword} 
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="En az 6 karakter"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowResetPassword(false); setNewPassword(''); }}>İptal</Button>
              <Button onClick={handleResetPassword} className="bg-terracotta text-white hover:bg-terracotta/90">Şifreyi Sıfırla</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Üye Düzenle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>İsim</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input value={editForm.phone} onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))} />
            </div>
            <div>
              <Label>Sadakat Puanı</Label>
              <Input type="number" value={editForm.loyalty_points} onChange={(e) => setEditForm(p => ({ ...p, loyalty_points: parseInt(e.target.value) || 0 }))} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>İptal</Button>
              <Button onClick={handleEditUser} className="bg-basil text-white hover:bg-basil/90">Kaydet</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default UsersManagement;
